import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { TabView, TabPanel } from "primereact/tabview";
import SimpleModal from "../Modals/SimpleModal";
import { useThemeStore } from "../../store/themeStore";
import {
  isDarkTheme,
  getAvailableThemes,
  themeSelectorNeeded,
  resolveToggleTarget,
} from "../../config/themes";
import { useAuthStore } from "../../store/authStore";
import { forceReloadWithoutCache } from "../../utils/forceReload";
import { useConfigGroups } from "./useConfigGroups";

const themeSelectorStyle = { marginLeft: "auto" };

const ThemeSelectorRow = () => {
  const { theme, toggleTheme, setTheme } = useThemeStore();
  // Lista fixa por sessão: runtime-config é aplicado antes do 1º render (main.jsx).
  // Dropdown (React.memo) exige options/handlers com referência estável.
  const themeOptions = useMemo(
    () => getAvailableThemes().map((t) => ({ label: t.label, value: t.name })),
    [],
  );
  const showThemeSelector = useMemo(() => themeSelectorNeeded(), []);
  const handleThemeSelect = useCallback((e) => setTheme(e.value), [setTheme]);
  // Toggle sem alvo (deploy single-theme ou sem polaridade oposta) fica
  // desabilitado — nunca um controle vivo que não faz nada.
  const toggleDead = resolveToggleTarget(theme) === theme;

  return (
    <div className="config-item config-theme-row">
      {showThemeSelector ? (
        // 3+ temas (ou par de mesma polaridade): dropdown único e claro —
        // o toggle sozinho não alcança todos, então não exibimos os dois.
        <>
          <i
            className={`pi ${isDarkTheme(theme) ? "pi-moon" : "pi-sun"}`}
            style={{ fontSize: 16 }}
            aria-hidden="true"
          />
          <label htmlFor="theme-selector" style={{ userSelect: "none" }}>
            Tema
          </label>
          <Dropdown
            inputId="theme-selector"
            value={theme}
            options={themeOptions}
            onChange={handleThemeSelect}
            className="p-inputtext-sm theme-selector-dropdown"
            style={themeSelectorStyle}
            data-evo-id="navbar-config-theme-selector"
          />
        </>
      ) : (
        // Par simples dark/light: toggle é o controle mais direto.
        <div className="config-theme-toggle">
          <Button
            icon={`pi ${isDarkTheme(theme) ? "pi-sun" : "pi-moon"}`}
            className="p-button-rounded p-button-sm theme-toggle-button"
            onClick={toggleTheme}
            disabled={toggleDead}
            aria-label="Toggle Theme"
            style={{ width: "28px", height: "28px" }}
            data-evo-id="navbar-config-theme-toggle"
          />
          <span
            className="theme-toggle-label"
            onClick={toggleDead ? undefined : toggleTheme}
            style={{
              userSelect: "none",
              cursor: toggleDead ? "default" : "pointer",
              opacity: toggleDead ? 0.5 : 1,
            }}
            data-evo-id="navbar-config-theme-label"
          >
            {isDarkTheme(theme) ? "Tema Light" : "Tema Dark"}
          </span>
        </div>
      )}
    </div>
  );
};

// Tooltip nas extremidades ancora na borda do botão para não vazar do modal
// (o scroll container clipa o que passa da largura — #374).
const edgeTooltipAlign = (index, count) => {
  if (index === 0) return "left";
  if (index === count - 1) return "right";
  return undefined;
};

const ConfigChoiceGroup = ({ item }) => (
  <div className="config-choice-block">
    <label>{item.label}</label>
    <div className="config-choice-group">
      {item.options.map((option, index) => (
        <button
          key={option.value}
          className={`config-choice-option ${item.value === option.value ? "selected" : ""}`}
          data-tooltip={option.tooltip}
          data-tooltip-align={
            option.tooltip
              ? edgeTooltipAlign(index, item.options.length)
              : undefined
          }
          onClick={() => item.onChange(option.value)}
          data-evo-id={item.evoId}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const configItemShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["toggle", "choice", "theme", "action"]).isRequired,
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      tooltip: PropTypes.string,
    }),
  ),
  evoId: PropTypes.string,
  icon: PropTypes.string,
  tooltip: PropTypes.string,
  onClick: PropTypes.func,
});

const ConfigItem = ({ item }) => {
  switch (item.type) {
    case "toggle":
      return (
        <div className="config-item">
          <Checkbox
            inputId={item.id}
            checked={item.value}
            onChange={(e) => item.onChange(e.checked)}
            data-evo-id={item.evoId}
          />
          <label htmlFor={item.id}>{item.label}</label>
        </div>
      );
    case "choice":
      return <ConfigChoiceGroup item={item} />;
    case "theme":
      return <ThemeSelectorRow />;
    case "action":
      return (
        <div className="config-divider-top">
          <Button
            label={item.label}
            icon={item.icon}
            className="p-button-outlined p-button-secondary p-button-sm config-action-button"
            onClick={item.onClick}
            tooltip={item.tooltip}
            tooltipOptions={{ position: "bottom" }}
            data-evo-id={item.evoId}
          />
        </div>
      );
    default:
      return null;
  }
};

ConfigChoiceGroup.propTypes = { item: configItemShape.isRequired };
ConfigItem.propTypes = { item: configItemShape.isRequired };

// Última aba aberta — memória de sessão (não persiste).
let lastConfigTab = 0;

const ConfigModalContent = ({ onForceReload }) => {
  const [activeTab, setActiveTab] = useState(() => lastConfigTab);
  const groups = useConfigGroups({ onForceReload });

  const handleTabChange = useCallback((e) => {
    lastConfigTab = e.index;
    setActiveTab(e.index);
  }, []);

  return (
    <TabView
      activeIndex={activeTab}
      onTabChange={handleTabChange}
      className="config-tabview"
    >
      {groups.map((group) => (
        <TabPanel key={group.id} header={group.label}>
          <div className="config-content">
            {group.items.map((item) => (
              <ConfigItem key={item.id} item={item} />
            ))}
          </div>
        </TabPanel>
      ))}
    </TabView>
  );
};

ConfigModalContent.propTypes = { onForceReload: PropTypes.func.isRequired };

const ConfigPanel = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const isAuthenticatedViaBrokerAuth = useAuthStore(
    (state) => state.isAuthenticatedViaBrokerAuth,
  );

  const handleForceReload = useCallback(() => {
    forceReloadWithoutCache({ viaBrokerAuth: isAuthenticatedViaBrokerAuth() });
  }, [isAuthenticatedViaBrokerAuth]);

  return (
    <>
      <span data-tooltip="Configurações">
        <Button
          icon="pi pi-cog"
          className="config-button navbar-button"
          onClick={() => setModalVisible(true)}
          aria-label="Configurações"
          data-evo-id="navbar-config-open"
        />
      </span>

      <SimpleModal
        title="Configurações"
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        onConfirm={() => setModalVisible(false)}
        size="small"
        showCancel={false}
        confirmLabel="Fechar"
        footer={null}
        className="config-modal"
        maskClassName="config-modal-mask"
      >
        <ConfigModalContent onForceReload={handleForceReload} />
      </SimpleModal>
    </>
  );
};

export default React.memo(ConfigPanel);
